import { type FormEvent, useState } from "react";
import { Brand } from "./Brand";
import type { Locale } from "./i18n";

type Copy = (key: string, values?: Record<string, string | number>) => string;

interface OnboardingDraft {
  operatorKey: string;
  merchantSlug: string;
  legalName: string;
  currency: string;
  category: "cafe_tea" | "food" | "shops";
  merchantNameEn: string;
  merchantNameZhHant: string;
  ownerEmail: string;
  ownerName: string;
  ownerLocale: Locale;
  locationSlug: string;
  locationInternalName: string;
  locationNameEn: string;
  locationNameZhHant: string;
  timezone: string;
  addressLine1: string;
  locality: string;
  region: string;
  postalCode: string;
  countryCode: string;
  latitude: string;
  longitude: string;
}

export interface PilotMerchantRequest {
  slug: string;
  legalName: string;
  defaultCurrency: string;
  category: "cafe_tea" | "food" | "shops";
  localizations: {
    en: { displayName: string };
    "zh-Hant"?: { displayName: string };
  };
  owner: {
    email: string;
    displayName: string;
    preferredLocale: Locale;
  };
  location: {
    slug: string;
    internalName: string;
    timezone: string;
    addressLine1: string;
    addressLine2: null;
    locality: string;
    region: string | null;
    postalCode: string;
    countryCode: string;
    latitude: number;
    longitude: number;
    localizations: {
      en: { displayName: string };
      "zh-Hant"?: { displayName: string };
    };
  };
}

interface PilotMerchantReceipt {
  merchant: { id: string; slug: string };
  owner: { id: string; email: string };
  location: {
    id: string;
    slug: string;
    joinUrl: string;
    publicReady: boolean;
  };
  idempotentReplay: boolean;
}

type SubmissionState =
  | { status: "idle" }
  | { status: "submitting" }
  | { status: "error"; authorization: boolean }
  | { status: "complete"; receipt: PilotMerchantReceipt };

const initialDraft: OnboardingDraft = {
  operatorKey: "",
  merchantSlug: "",
  legalName: "",
  currency: "EUR",
  category: "shops",
  merchantNameEn: "",
  merchantNameZhHant: "",
  ownerEmail: "",
  ownerName: "",
  ownerLocale: "en",
  locationSlug: "",
  locationInternalName: "Main",
  locationNameEn: "",
  locationNameZhHant: "",
  timezone: "Europe/Berlin",
  addressLine1: "",
  locality: "Berlin",
  region: "Berlin",
  postalCode: "",
  countryCode: "DE",
  latitude: "",
  longitude: "",
};

function optionalLocalization(value: string) {
  const displayName = value.trim();
  return displayName ? { "zh-Hant": { displayName } } : {};
}

export function normalizePilotMerchantDraft(
  draft: OnboardingDraft,
): PilotMerchantRequest | null {
  const latitude = Number(draft.latitude);
  const longitude = Number(draft.longitude);
  const required = [
    draft.merchantSlug,
    draft.legalName,
    draft.currency,
    draft.merchantNameEn,
    draft.ownerEmail,
    draft.ownerName,
    draft.locationSlug,
    draft.locationInternalName,
    draft.locationNameEn,
    draft.timezone,
    draft.addressLine1,
    draft.locality,
    draft.postalCode,
    draft.countryCode,
    draft.latitude,
    draft.longitude,
  ];
  if (
    required.some((value) => !value.trim()) ||
    !Number.isFinite(latitude) ||
    latitude < -90 ||
    latitude > 90 ||
    !Number.isFinite(longitude) ||
    longitude < -180 ||
    longitude > 180
  ) {
    return null;
  }
  return {
    slug: draft.merchantSlug.trim().toLowerCase(),
    legalName: draft.legalName.trim(),
    defaultCurrency: draft.currency.trim().toUpperCase(),
    category: draft.category,
    localizations: {
      en: { displayName: draft.merchantNameEn.trim() },
      ...optionalLocalization(draft.merchantNameZhHant),
    },
    owner: {
      email: draft.ownerEmail.trim().toLowerCase(),
      displayName: draft.ownerName.trim(),
      preferredLocale: draft.ownerLocale,
    },
    location: {
      slug: draft.locationSlug.trim().toLowerCase(),
      internalName: draft.locationInternalName.trim(),
      timezone: draft.timezone.trim(),
      addressLine1: draft.addressLine1.trim(),
      addressLine2: null,
      locality: draft.locality.trim(),
      region: draft.region.trim() || null,
      postalCode: draft.postalCode.trim(),
      countryCode: draft.countryCode.trim().toUpperCase(),
      latitude,
      longitude,
      localizations: {
        en: { displayName: draft.locationNameEn.trim() },
        ...optionalLocalization(draft.locationNameZhHant),
      },
    },
  };
}

function Field({
  id,
  label,
  value,
  onChange,
  type = "text",
  autoComplete,
  required = true,
  inputMode,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  type?: "text" | "email" | "password";
  autoComplete?: string;
  required?: boolean;
  inputMode?: "decimal";
}) {
  return (
    <label className="operator-field" htmlFor={id}>
      <span>{label}</span>
      <input
        id={id}
        type={type}
        value={value}
        autoComplete={autoComplete}
        inputMode={inputMode}
        required={required}
        onChange={(event) => onChange(event.target.value)}
      />
    </label>
  );
}

export function OperatorOnboarding({
  copy,
  locale,
  onLocale,
  adminMode = false,
}: {
  adminMode?: boolean;
  copy: Copy;
  locale: Locale;
  onLocale: (locale: Locale) => void;
}) {
  const [draft, setDraft] = useState(initialDraft);
  const [idempotencyKey, setIdempotencyKey] = useState<string | null>(null);
  const [submission, setSubmission] = useState<SubmissionState>({
    status: "idle",
  });

  const update = <K extends keyof OnboardingDraft>(
    field: K,
    value: OnboardingDraft[K],
  ) => {
    setDraft((current) => ({ ...current, [field]: value }));
    setIdempotencyKey(null);
    setSubmission({ status: "idle" });
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const input = normalizePilotMerchantDraft(draft);
    if (!input || (!adminMode && draft.operatorKey.trim().length < 24)) {
      setSubmission({ status: "error", authorization: false });
      return;
    }
    const key =
      idempotencyKey ??
      `idem_operator_ui_${crypto.randomUUID().replaceAll("-", "")}`;
    setIdempotencyKey(key);
    setSubmission({ status: "submitting" });
    try {
      const response = await fetch(
        adminMode ? "/v1/admin/merchants" : "/v1/operator/pilot-merchants",
        {
          method: "POST",
          headers: {
            ...(!adminMode
              ? { authorization: `Bearer ${draft.operatorKey.trim()}` }
              : {}),
            "content-type": "application/json",
            "idempotency-key": key,
          },
          body: JSON.stringify(input),
        },
      );
      if (!response.ok) {
        setSubmission({
          status: "error",
          authorization: response.status === 401,
        });
        return;
      }
      const receipt = (await response.json()) as PilotMerchantReceipt;
      setDraft((current) => ({ ...current, operatorKey: "" }));
      setSubmission({ status: "complete", receipt });
    } catch {
      setSubmission({ status: "error", authorization: false });
    }
  };

  if (submission.status === "complete") {
    return (
      <main className="operator-screen">
        <header className="operator-toolbar">
          <Brand />
          <LocaleSelect
            copy={copy}
            locale={locale}
            onLocale={onLocale}
            adminMode={adminMode}
          />
        </header>
        <section className="operator-complete" aria-live="polite">
          <div className="success-mark" aria-hidden="true">
            ✓
          </div>
          <p className="eyebrow">{copy("merchant.operator.completeEyebrow")}</p>
          <h1>{copy("merchant.operator.completeTitle")}</h1>
          <p>
            {copy("merchant.operator.completeBody", {
              email: submission.receipt.owner.email,
            })}
          </p>
          <dl>
            <div>
              <dt>{copy("merchant.operator.merchantSlug")}</dt>
              <dd>{submission.receipt.merchant.slug}</dd>
            </div>
            <div>
              <dt>{copy("merchant.operator.joinUrl")}</dt>
              <dd>{submission.receipt.location.joinUrl}</dd>
            </div>
          </dl>
          <a className="primary-button" href="/">
            {copy(adminMode ? "admin.back" : "merchant.operator.openSignIn")}
          </a>
        </section>
      </main>
    );
  }

  return (
    <main className="operator-screen">
      <header className="operator-toolbar">
        <Brand />
        <LocaleSelect
          copy={copy}
          locale={locale}
          onLocale={onLocale}
          adminMode={adminMode}
        />
      </header>
      <div className="operator-intro">
        <p className="eyebrow">
          {copy(adminMode ? "admin.internal" : "merchant.operator.eyebrow")}
        </p>
        <h1>
          {copy(adminMode ? "admin.onboardTitle" : "merchant.operator.title")}
        </h1>
        <p>
          {copy(adminMode ? "admin.onboardBody" : "merchant.operator.body")}
        </p>
      </div>
      <form className="operator-form" onSubmit={submit}>
        {!adminMode && (
          <section className="panel operator-section operator-access">
            <div>
              <p className="eyebrow">
                {copy("merchant.operator.accessEyebrow")}
              </p>
              <h2>{copy("merchant.operator.accessTitle")}</h2>
              <p>{copy("merchant.operator.accessBody")}</p>
            </div>
            <Field
              id="operator-key"
              label={copy("merchant.operator.operatorKey")}
              value={draft.operatorKey}
              type="password"
              autoComplete="off"
              onChange={(value) => update("operatorKey", value)}
            />
          </section>
        )}

        <section className="panel operator-section">
          <div className="operator-section-heading">
            <span>01</span>
            <div>
              <p className="eyebrow">{copy("merchant.operator.shopEyebrow")}</p>
              <h2>{copy("merchant.operator.shopTitle")}</h2>
            </div>
          </div>
          <div className="operator-grid">
            <Field
              id="merchant-name-en"
              label={copy("merchant.operator.merchantNameEn")}
              value={draft.merchantNameEn}
              onChange={(value) => update("merchantNameEn", value)}
            />
            <Field
              id="merchant-name-zh"
              label={copy("merchant.operator.merchantNameZhHant")}
              value={draft.merchantNameZhHant}
              required={false}
              onChange={(value) => update("merchantNameZhHant", value)}
            />
            <Field
              id="merchant-legal-name"
              label={copy("merchant.operator.legalName")}
              value={draft.legalName}
              onChange={(value) => update("legalName", value)}
            />
            <Field
              id="merchant-slug"
              label={copy("merchant.operator.merchantSlug")}
              value={draft.merchantSlug}
              onChange={(value) =>
                update(
                  "merchantSlug",
                  value.toLowerCase().replace(/[^a-z0-9-]/g, ""),
                )
              }
            />
            <Field
              id="merchant-currency"
              label={copy("merchant.operator.currency")}
              value={draft.currency}
              onChange={(value) => update("currency", value.slice(0, 3))}
            />
            <label className="operator-field" htmlFor="merchant-category">
              <span>{copy("merchant.operator.category")}</span>
              <select
                id="merchant-category"
                value={draft.category}
                onChange={(event) =>
                  update(
                    "category",
                    event.target.value as OnboardingDraft["category"],
                  )
                }
              >
                <option value="cafe_tea">
                  {copy("merchant.operator.categoryCafeTea")}
                </option>
                <option value="food">
                  {copy("merchant.operator.categoryFood")}
                </option>
                <option value="shops">
                  {copy("merchant.operator.categoryShops")}
                </option>
              </select>
            </label>
          </div>
        </section>

        <section className="panel operator-section">
          <div className="operator-section-heading">
            <span>02</span>
            <div>
              <p className="eyebrow">
                {copy("merchant.operator.ownerEyebrow")}
              </p>
              <h2>{copy("merchant.operator.ownerTitle")}</h2>
            </div>
          </div>
          <div className="operator-grid">
            <Field
              id="owner-name"
              label={copy("merchant.operator.ownerName")}
              value={draft.ownerName}
              autoComplete="name"
              onChange={(value) => update("ownerName", value)}
            />
            <Field
              id="owner-email"
              label={copy("merchant.operator.ownerEmail")}
              value={draft.ownerEmail}
              type="email"
              autoComplete="email"
              onChange={(value) => update("ownerEmail", value)}
            />
            <label className="operator-field" htmlFor="owner-locale">
              <span>{copy("merchant.operator.ownerLocale")}</span>
              <select
                id="owner-locale"
                value={draft.ownerLocale}
                onChange={(event) =>
                  update("ownerLocale", event.target.value as Locale)
                }
              >
                <option value="en">English</option>
                <option value="zh-Hant">繁體中文</option>
              </select>
            </label>
          </div>
        </section>

        <section className="panel operator-section">
          <div className="operator-section-heading">
            <span>03</span>
            <div>
              <p className="eyebrow">
                {copy("merchant.operator.locationEyebrow")}
              </p>
              <h2>{copy("merchant.operator.locationTitle")}</h2>
            </div>
          </div>
          <div className="operator-grid">
            <Field
              id="location-name-en"
              label={copy("merchant.operator.locationNameEn")}
              value={draft.locationNameEn}
              onChange={(value) => update("locationNameEn", value)}
            />
            <Field
              id="location-name-zh"
              label={copy("merchant.operator.locationNameZhHant")}
              value={draft.locationNameZhHant}
              required={false}
              onChange={(value) => update("locationNameZhHant", value)}
            />
            <Field
              id="location-internal-name"
              label={copy("merchant.operator.locationInternalName")}
              value={draft.locationInternalName}
              onChange={(value) => update("locationInternalName", value)}
            />
            <Field
              id="location-slug"
              label={copy("merchant.operator.locationSlug")}
              value={draft.locationSlug}
              onChange={(value) =>
                update(
                  "locationSlug",
                  value.toLowerCase().replace(/[^a-z0-9-]/g, ""),
                )
              }
            />
            <Field
              id="location-address"
              label={copy("merchant.operator.address")}
              value={draft.addressLine1}
              onChange={(value) => update("addressLine1", value)}
            />
            <Field
              id="location-locality"
              label={copy("merchant.operator.locality")}
              value={draft.locality}
              onChange={(value) => update("locality", value)}
            />
            <Field
              id="location-region"
              label={copy("merchant.operator.region")}
              value={draft.region}
              required={false}
              onChange={(value) => update("region", value)}
            />
            <Field
              id="location-postal-code"
              label={copy("merchant.operator.postalCode")}
              value={draft.postalCode}
              onChange={(value) => update("postalCode", value)}
            />
            <Field
              id="location-country-code"
              label={copy("merchant.operator.countryCode")}
              value={draft.countryCode}
              onChange={(value) => update("countryCode", value.slice(0, 2))}
            />
            <Field
              id="location-timezone"
              label={copy("merchant.operator.timezone")}
              value={draft.timezone}
              onChange={(value) => update("timezone", value)}
            />
            <Field
              id="location-latitude"
              label={copy("merchant.operator.latitude")}
              value={draft.latitude}
              inputMode="decimal"
              onChange={(value) => update("latitude", value)}
            />
            <Field
              id="location-longitude"
              label={copy("merchant.operator.longitude")}
              value={draft.longitude}
              inputMode="decimal"
              onChange={(value) => update("longitude", value)}
            />
          </div>
        </section>

        {submission.status === "error" && (
          <p className="operator-error" role="alert">
            {copy(
              submission.authorization
                ? "merchant.operator.authorizationError"
                : "merchant.operator.submitError",
            )}
          </p>
        )}
        <div className="operator-submit">
          <p>{copy("merchant.operator.submitNote")}</p>
          <button
            className="primary-button"
            type="submit"
            disabled={submission.status === "submitting"}
          >
            {copy(
              submission.status === "submitting"
                ? "merchant.operator.submitting"
                : adminMode
                  ? "admin.createMerchant"
                  : "merchant.operator.submit",
            )}
          </button>
        </div>
      </form>
    </main>
  );
}

function LocaleSelect({
  copy,
  locale,
  onLocale,
  adminMode = false,
}: {
  adminMode?: boolean;
  copy: Copy;
  locale: Locale;
  onLocale: (locale: Locale) => void;
}) {
  return (
    <select
      className="quiet-button"
      value={locale}
      aria-label={copy("language.choose")}
      onChange={(event) => onLocale(event.target.value as Locale)}
    >
      <option value="en">{copy("language.english")}</option>
      <option value="zh-Hant">{copy("language.traditionalChinese")}</option>
      {!adminMode && <option value="de">{copy("language.german")}</option>}
    </select>
  );
}
